import * as React from "react";
import { Search, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

export type FilterBarProps = {
  searchValue: string;
  onSearchChange: (value: string) => void;
  /** Names the field for assistive technology; shown only to screen readers. */
  searchLabel?: string;
  searchPlaceholder?: string;
  /** Selects, toggles, and date ranges. Each control labels itself. */
  children?: React.ReactNode;
  /**
   * Result count. Announced politely so filtering tells a screen-reader user
   * what changed without moving focus.
   */
  summary?: React.ReactNode;
  /** Shown only when at least one filter is applied. */
  onClear?: () => void;
  clearLabel?: string;
  /** Page-level actions such as "New project". */
  actions?: React.ReactNode;
  className?: string;
  /** Names the whole region when a screen has more than one filter bar. */
  label?: string;
};

/**
 * The filter bar for every list screen: one search field, any number of filter
 * controls, a live result count, and the page actions.
 *
 * Filtering never changes focus, so a keyboard user can keep typing while the
 * results behind the bar update.
 */
export function FilterBar({
  searchValue,
  onSearchChange,
  searchLabel = "Search",
  searchPlaceholder = "Search",
  children,
  summary,
  onClear,
  clearLabel = "Clear filters",
  actions,
  className,
  label = "Filters",
}: FilterBarProps) {
  const searchId = React.useId();

  return (
    <div
      role="search"
      aria-label={label}
      className={cn(
        "flex flex-col gap-3 rounded-lg border bg-background p-3 lg:flex-row lg:items-center lg:justify-between",
        className,
      )}
    >
      <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative min-w-0 flex-1 sm:max-w-xs">
          <Label htmlFor={searchId} className="sr-only">
            {searchLabel}
          </Label>
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            id={searchId}
            type="search"
            value={searchValue}
            placeholder={searchPlaceholder}
            className="pl-9"
            onChange={(event) => onSearchChange(event.target.value)}
          />
        </div>

        {children}

        {onClear && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClear}
            className="shrink-0"
          >
            <X className="h-4 w-4" aria-hidden="true" />
            {clearLabel}
          </Button>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 lg:justify-end">
        {summary !== undefined && (
          <p
            aria-live="polite"
            className="text-xs text-muted-foreground"
          >
            {summary}
          </p>
        )}
        {actions && (
          <div className="flex shrink-0 items-center gap-2">{actions}</div>
        )}
      </div>
    </div>
  );
}

export type FilterFieldProps = {
  label: string;
  /** Receives the generated id so the control is labelled without a wrapper. */
  children: (controlId: string) => React.ReactNode;
  className?: string;
  /** Keeps the label visible instead of exposing it to screen readers only. */
  showLabel?: boolean;
};

/**
 * Labels a filter control. Radix `Select` renders a button rather than a
 * `<select>`, so the label is wired by id instead of by nesting.
 */
export function FilterField({
  label,
  children,
  className,
  showLabel = false,
}: FilterFieldProps) {
  const controlId = React.useId();

  return (
    <div className={cn("flex min-w-0 flex-col gap-1", className)}>
      <Label htmlFor={controlId} className={showLabel ? "text-xs" : "sr-only"}>
        {label}
      </Label>
      {children(controlId)}
    </div>
  );
}
